/**
 * Recurring-listing rules, shared by the submit form (live preview) and the
 * server (authoritative expansion). Works on local calendar dates
 * ('YYYY-MM-DD'); times and timezones are applied by the caller.
 */
export type Weekday = 0 | 1 | 2 | 3 | 4 | 5 | 6; // 0 = Sunday

export type Recurrence =
  | { freq: 'weekly'; interval: 1 | 2 | 3 | 4; weekdays: Weekday[] }
  | { freq: 'monthly_nth'; nth: 1 | 2 | 3 | 4 | -1; weekday: Weekday }
  | { freq: 'monthly_day'; day: number };

export const RECURRENCE_LIMITS = { maxDays: 366, maxOccurrences: 60 } as const;

export const WEEKDAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'] as const;
export const WEEKDAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;
const NTH = { 1: '1st', 2: '2nd', 3: '3rd', 4: '4th', [-1]: 'last' } as Record<number, string>;

const DAY_MS = 86_400_000;

function toUtc(d: string): number {
  const [y, m, dd] = d.split('-').map(Number) as [number, number, number];
  return Date.UTC(y, m - 1, dd);
}
const fmt = (t: number) => new Date(t).toISOString().slice(0, 10);

export function weekdayOf(date: string): Weekday {
  return new Date(toUtc(date)).getUTCDay() as Weekday;
}

export function addDays(date: string, n: number): string {
  return fmt(toUtc(date) + n * DAY_MS);
}

function ordinal(n: number): string {
  const s = n % 100 >= 11 && n % 100 <= 13 ? 'th' : ({ 1: 'st', 2: 'nd', 3: 'rd' } as Record<number, string>)[n % 10] ?? 'th';
  return `${n}${s}`;
}

/** Date (UTC ms) of the nth (or last, -1) given weekday in a month, or null if it doesn't exist. */
function nthWeekdayOfMonth(y: number, m0: number, nth: number, wd: number): number | null {
  if (nth === -1) {
    const last = new Date(Date.UTC(y, m0 + 1, 0));
    const back = (last.getUTCDay() - wd + 7) % 7;
    return Date.UTC(y, m0, last.getUTCDate() - back);
  }
  const first = new Date(Date.UTC(y, m0, 1)).getUTCDay();
  const day = 1 + ((wd - first + 7) % 7) + (nth - 1) * 7;
  const t = Date.UTC(y, m0, day);
  return new Date(t).getUTCMonth() === m0 ? t : null;
}

function matches(t: number, startT: number, rule: Recurrence): boolean {
  const d = new Date(t);
  if (rule.freq === 'weekly') {
    if (!rule.weekdays.includes(d.getUTCDay() as Weekday)) return false;
    // week index counted from the Sunday on/before the start date
    const startSunday = startT - new Date(startT).getUTCDay() * DAY_MS;
    const week = Math.floor((t - startSunday) / (7 * DAY_MS));
    return week % rule.interval === 0;
  }
  if (rule.freq === 'monthly_nth') {
    return nthWeekdayOfMonth(d.getUTCFullYear(), d.getUTCMonth(), rule.nth, rule.weekday) === t;
  }
  return d.getUTCDate() === rule.day;
}

export function describeRecurrence(rule: Recurrence): string {
  if (rule.freq === 'weekly') {
    const days = [...rule.weekdays].sort((a, b) => a - b);
    const names = days.length === 1 ? WEEKDAY_NAMES[days[0]!] : days.map((d) => WEEKDAY_SHORT[d]).join(' & ');
    if (rule.interval === 1) return days.length === 1 ? `Every ${names}` : `${names} weekly`;
    return `Every ${rule.interval} weeks on ${days.map((d) => WEEKDAY_SHORT[d]).join(' & ')}`;
  }
  if (rule.freq === 'monthly_nth') return `${NTH[rule.nth]} ${WEEKDAY_NAMES[rule.weekday]} monthly`.replace(/^./, (c) => c.toUpperCase());
  return `Monthly on the ${ordinal(rule.day)}`;
}

/** Short form for cards: "2nd Sat monthly", "Every Sat", "Every 2 weeks". */
export function shortRecurrence(rule: Recurrence): string {
  if (rule.freq === 'weekly') {
    const days = [...rule.weekdays].sort((a, b) => a - b).map((d) => WEEKDAY_SHORT[d]).join(' & ');
    return rule.interval === 1 ? `Every ${days}` : `Every ${rule.interval} wks · ${days}`;
  }
  if (rule.freq === 'monthly_nth') return `${NTH[rule.nth]} ${WEEKDAY_SHORT[rule.weekday]} monthly`.replace(/^./, (c) => c.toUpperCase());
  return `Monthly · ${ordinal(rule.day)}`;
}

export interface Expansion {
  dates: string[];
  error: string | null;
}

/** All dates from `start` through `until` (inclusive) matching `rule`, with validation. */
export function expandRecurrence(start: string, rule: Recurrence, until: string): Expansion {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(start) || !/^\d{4}-\d{2}-\d{2}$/.test(until)) {
    return { dates: [], error: 'Pick a start date and an end date' };
  }
  const s = toUtc(start);
  const u = toUtc(until);
  if (u < s) return { dates: [], error: 'The repeat end date must be after the first date' };
  if ((u - s) / DAY_MS > RECURRENCE_LIMITS.maxDays) return { dates: [], error: 'Repeats can run for up to 12 months' };
  if (rule.freq === 'weekly' && rule.weekdays.length === 0) return { dates: [], error: 'Pick at least one day of the week' };
  if (rule.freq === 'monthly_day' && (rule.day < 1 || rule.day > 31)) return { dates: [], error: 'Pick a day of the month from 1 to 31' };
  if (!matches(s, s, rule)) {
    return { dates: [], error: `The first date isn't ${describeRecurrence(rule).toLowerCase().replace(/^every /, 'a ')} — pick a first date that matches` };
  }
  const dates: string[] = [];
  for (let t = s; t <= u; t += DAY_MS) {
    if (matches(t, s, rule)) {
      dates.push(fmt(t));
      if (dates.length > RECURRENCE_LIMITS.maxOccurrences) {
        return { dates: [], error: `That's more than ${RECURRENCE_LIMITS.maxOccurrences} dates — shorten the range` };
      }
    }
  }
  return { dates, error: null };
}

/** The first date on/after `from` that matches the rule (used to snap the form's first date). */
export function firstMatchOnOrAfter(from: string, rule: Recurrence): string | null {
  const s = toUtc(from);
  for (let i = 0; i < 400; i++) {
    const t = s + i * DAY_MS;
    if (rule.freq === 'weekly' ? rule.weekdays.includes(new Date(t).getUTCDay() as Weekday) : matches(t, t, rule)) return fmt(t);
  }
  return null;
}
